import { storeRequestHeader } from "@go-novus/admin-api";

import { boundShopDomain } from "#/shared/lib/bound-store";

export type BackendJsonMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

export type BackendFetch = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

export interface BackendResponseErrorCause {
  body?: unknown;
  mediaType?: string;
  readonly status: number;
  text?: string;
}

export interface BackendRequestOptions {
  body?: BodyInit | null;
  fetch?: BackendFetch;
  headers?: HeadersInit;
  method?: BackendJsonMethod;
  signal?: AbortSignal;
}

export type BackendJsonRequestOptions = Omit<BackendRequestOptions, "method">;

export class BackendHttpError extends Error {
  readonly responseCause: BackendResponseErrorCause;
  readonly status: number;

  constructor(responseCause: BackendResponseErrorCause) {
    const status = responseCause.status;
    super(`backend request failed: ${status}`);
    this.name = "BackendHttpError";
    this.responseCause = responseCause;
    this.status = status;
  }
}

export class BackendResponseParseError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown, message = "backend response parse failed") {
    super(message);
    this.name = "BackendResponseParseError";
    this.cause = cause;
  }
}

export class BackendJsonParseError extends BackendResponseParseError {
  constructor(cause: unknown) {
    super(cause, "backend json response parse failed");
    this.name = "BackendJsonParseError";
  }
}

export function readBackendHttpErrorPayload(error: BackendHttpError): unknown {
  if (error.responseCause.body !== undefined) {
    return error.responseCause.body;
  }

  if (
    error.responseCause.mediaType === "text/plain" &&
    error.responseCause.text !== undefined
  ) {
    return error.responseCause.text;
  }

  return error.responseCause;
}

function createBackendRequestHeaders(headers?: HeadersInit): Headers {
  const requestHeaders = new Headers();

  if (headers !== undefined) {
    for (const [name, value] of new Headers(headers)) {
      requestHeaders.set(name, value);
    }
  }

  return requestHeaders;
}

async function readBackendResponseErrorCause(
  response: Response,
): Promise<BackendResponseErrorCause> {
  const status = response.status;
  const contentType = response.headers.get("content-type") ?? "";
  const mediaType = contentType.split(";", 1)[0]?.trim().toLowerCase();

  if (contentType.includes("application/json")) {
    try {
      return {
        body: (await response.json()) as unknown,
        status,
      };
    } catch (error: unknown) {
      return { body: error, status };
    }
  }

  try {
    return {
      ...(mediaType ? { mediaType } : {}),
      status,
      text: await response.text(),
    };
  } catch (error: unknown) {
    return { body: error, status };
  }
}

/**
 * 后台与 admin JSON API 同源；独立入口带登录 Cookie，内嵌入口由 App Bridge
 * 的 fetch 拦截器附加 Shopify ID token，不依赖第三方 Cookie。
 * 每个请求都带 `X-GoNovus-Store` 头声明本页所属店铺——同一浏览器能登录
 * 多家店，服务端靠它在各店 Cookie 里挑出正确的那枚；调用方不能自己设。
 */
export async function requestBackendResponse(
  path: string,
  options: BackendRequestOptions = {},
): Promise<Response> {
  const fetchImpl =
    options.fetch ??
    ((input: string, init?: RequestInit) => globalThis.fetch(input, init));
  const headers = createBackendRequestHeaders(options.headers);
  const shop = boundShopDomain();
  if (shop !== null) headers.set(storeRequestHeader, shop);
  const response = await fetchImpl(path, {
    body: options.body,
    credentials: "same-origin",
    headers,
    method: options.method ?? "GET",
    signal: options.signal,
  });

  if (!response.ok) {
    throw new BackendHttpError(await readBackendResponseErrorCause(response));
  }

  return response;
}

export async function requestBackendJson(
  path: string,
  method: BackendJsonMethod,
  options: BackendJsonRequestOptions = {},
): Promise<unknown> {
  const headers = createBackendRequestHeaders(options.headers);
  headers.set("Accept", "application/json");

  const response = await requestBackendResponse(path, {
    ...options,
    headers,
    method,
  });

  if (response.status === 204 || response.status === 205) {
    return undefined;
  }

  try {
    const responseText = await response.text();

    return responseText.length === 0
      ? undefined
      : (JSON.parse(responseText) as unknown);
  } catch (error: unknown) {
    throw new BackendJsonParseError(error);
  }
}
